// On-device OCR for medicine box photos. Runs entirely in the browser via
// tesseract.js (WASM); no image or extracted text is sent to any server.
// Output is always a suggestion for the user to review — see docs/AI_RULES.md.
export interface OcrResult {
  text: string;
  nameGuess: string | null;
  expiryIso: string | null;
  expiryHint: string | null;
  // tesseract's own confidence (0-100) for the winning rotation pass. Letter
  // density alone can't tell "Дибикор" from character salad tesseract
  // confidently invented on a stylized logo or a script it wasn't loaded
  // for — this is the one honest signal we have for "don't trust this",
  // used by the caller to decide whether to fall back to AI vision even
  // when a nameGuess was technically found.
  confidence: number;
}

const EXPIRY_KEYWORDS = ['годен', 'срок годн', 'exp', 'best before', 'use by', 'use before'];

const pad = (value: number) => String(value).padStart(2, '0');
const normalizeYear = (year: number) => (year < 100 ? 2000 + year : year);
const isPlausibleDate = (day: number, month: number, year: number) =>
  month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 2000 && year <= 2100;

function keywordScore(text: string, index: number): number {
  const before = text.slice(Math.max(0, index - 25), index).toLowerCase();
  return EXPIRY_KEYWORDS.some((keyword) => before.includes(keyword)) ? 1 : 0;
}

function findFullDates(text: string): { iso: string; index: number }[] {
  const re = /\b(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})\b/g;
  const found: { iso: string; index: number }[] = [];
  for (let match = re.exec(text); match; match = re.exec(text)) {
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = normalizeYear(Number(match[3]));
    if (isPlausibleDate(day, month, year)) {
      found.push({ iso: `${year}-${pad(month)}-${pad(day)}`, index: match.index });
    }
  }
  return found;
}

function findMonthYears(text: string): { label: string; key: number; index: number }[] {
  const re = /\b(\d{1,2})[.\-/](\d{4})\b/g;
  const found: { label: string; key: number; index: number }[] = [];
  for (let match = re.exec(text); match; match = re.exec(text)) {
    const month = Number(match[1]);
    const year = Number(match[2]);
    if (month >= 1 && month <= 12 && year >= 2000 && year <= 2100) {
      found.push({ label: `${pad(month)}.${year}`, key: year * 100 + month, index: match.index });
    }
  }
  return found;
}

// Exported for unit testing (src/lib/ocr.test.ts) — pure text parsing, no
// tesseract/canvas involved, unlike recognizeMedicinePhoto below.
export function guessExpiry(text: string): { expiryIso: string | null; expiryHint: string | null } {
  // A full day.month.year match is unambiguous enough to prefill the date field.
  // A bare month.year (common as "срок годности до MM.YYYY") is only ever shown
  // as a hint — picking a day within that month would be inventing data.
  const fullDates = findFullDates(text);
  if (fullDates.length) {
    const best = [...fullDates].sort(
      (a, b) => keywordScore(text, b.index) - keywordScore(text, a.index) || (b.iso > a.iso ? 1 : -1),
    )[0];
    return { expiryIso: best.iso, expiryHint: null };
  }
  const monthYears = findMonthYears(text);
  if (monthYears.length) {
    const best = [...monthYears].sort(
      (a, b) => keywordScore(text, b.index) - keywordScore(text, a.index) || b.key - a.key,
    )[0];
    return { expiryIso: null, expiryHint: best.label };
  }
  return { expiryIso: null, expiryHint: null };
}

export function guessName(text: string): string | null {
  // Score each line instead of taking the first regex match — a garbled
  // fragment (stray punctuation, a half-read logo) can otherwise win just
  // by appearing first. Prefer the line with the most letters among lines
  // that are mostly alphabetic, which is a much better proxy for "this is
  // the actual product name" than line order.
  const candidates = text
    .split('\n')
    .map((raw) => raw.trim())
    .filter((candidate) => candidate.length >= 3 && candidate.length <= 40 && !/^\d/.test(candidate));
  let best: { candidate: string; letters: number } | null = null;
  for (const candidate of candidates) {
    const letters = candidate.match(/[a-zA-Zа-яА-ЯёЁ]/g)?.length || 0;
    if (letters < 3 || letters / candidate.length < 0.6) continue;
    if (!best || letters > best.letters) best = { candidate, letters };
  }
  return best?.candidate || null;
}

async function rotateImage(image: File, degrees: 90 | 270): Promise<Blob> {
  const bitmap = await createImageBitmap(image);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.height;
  canvas.height = bitmap.width;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  bitmap.close?.();
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob failed'))), 'image/jpeg', 0.9);
  });
}

export async function recognizeMedicinePhoto(image: File): Promise<OcrResult> {
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('rus+eng');
  try {
    // Phones are commonly held sideways relative to the box, and tesseract
    // doesn't auto-rotate — try the photo as-is and both 90° rotations,
    // then keep whichever pass tesseract itself was most confident about.
    let bestText = '';
    let bestConfidence = -1;
    for (const degrees of [0, 90, 270] as const) {
      const input = degrees === 0 ? image : await rotateImage(image, degrees);
      const { data } = await worker.recognize(input);
      if (data.confidence > bestConfidence) {
        bestConfidence = data.confidence;
        bestText = data.text.trim();
      }
    }
    const { expiryIso, expiryHint } = guessExpiry(bestText);
    return { text: bestText, nameGuess: guessName(bestText), expiryIso, expiryHint, confidence: Math.max(0, bestConfidence) };
  } finally {
    await worker.terminate();
  }
}
